<i18n>
{
  "fr": {
    "BirthdateForm": {
      "first_name": "Prénom",
      "birthdate": "Date de naissance (JJ/MM/AAAA)",
      "gender": "Genre",
      "relationship": "Lien de parenté",
      "required": "*",
      "remove": "Supprimer cet anniversaire"
    }
  }
}
</i18n>

<template>
  <div class="birthdate-form">
    <div class="birthdate-form__row">
      <div
        class="label-input birthdate-form__input"
        :class="{ 'label-input--with-errors': firstNameErrors }"
      >
        <label :for="`${fieldName}[first_name]`" class="label-input__label">
          {{ $t('BirthdateForm.first_name') }}
          <span class="label-input__mandatory">
            {{ $t('BirthdateForm.required') }}
          </span>
        </label>

        <input
          :id="`${fieldName}[first_name]`"
          v-model="value.first_name"
          type="text"
          :name="`${fieldName}[first_name]`"
          required
          class="label-input__input"
          @input="handleInput"
        />

        <p v-if="firstNameErrors" class="label-input__errors">
          <span v-for="(error, index) in firstNameErrors" :key="index">
            {{ error }}
            <br v-show="index === firstNameErrors.length - 1" />
          </span>
        </p>
      </div>

      <div
        class="label-input birthdate-form__input"
        :class="{ 'label-input--with-errors': birthdateErrors }"
      >
        <label :for="`${fieldName}[birthdate]`" class="label-input__label">
          {{ $t('BirthdateForm.birthdate') }}
          <span class="label-input__mandatory">
            {{ $t('BirthdateForm.required') }}
          </span>
        </label>

        <input
          :id="`${fieldName}[birthdate]`"
          v-model="value.birthdate"
          type="date"
          :name="`${fieldName}[birthdate]`"
          required
          class="label-input__input birthdate-form__date"
          @input="handleInput"
        />

        <p v-if="birthdateErrors" class="label-input__errors">
          <span v-for="(error, index) in birthdateErrors" :key="index">
            {{ error }}
            <br v-show="index === birthdateErrors.length - 1" />
          </span>
        </p>
      </div>
    </div>

    <div class="birthdate-form__row">
      <div
        class="label-input birthdate-form__input"
        :class="{ 'label-input--with-errors': genderErrors }"
      >
        <label :for="`${fieldName}[gender]`" class="label-input__label">
          {{ $t('BirthdateForm.gender') }}
          <span class="label-input__mandatory">
            {{ $t('BirthdateForm.required') }}
          </span>
        </label>

        <div class="label-input__select">
          <select
            :id="`${fieldName}[gender]`"
            v-model="value.gender"
            class="birthdate-form__select"
            :name="`${fieldName}[gender]`"
            required
            @input="handleInput"
          >
            <option
              v-for="(key, option) in genderOptions"
              :key="option"
              :value="option"
              v-text="key"
            ></option>
          </select>
        </div>

        <p v-if="genderErrors" class="label-input__errors">
          <span v-for="(error, index) in genderErrors" :key="index">
            {{ error }}
            <br v-show="index === genderErrors.length - 1" />
          </span>
        </p>
      </div>

      <div
        class="label-input birthdate-form__input"
        :class="{
          'label-input--with-errors': relationshipErrors,
        }"
      >
        <label :for="`${fieldName}[relationship]`" class="label-input__label">
          {{ $t('BirthdateForm.relationship') }}
          <span class="label-input__mandatory">
            {{ $t('BirthdateForm.required') }}
          </span>
        </label>

        <div class="label-input__select">
          <select
            :id="`${fieldName}[relationship]`"
            v-model="value.relationship"
            class="birthdate-form__select"
            :name="`${fieldName}[relationship]`"
            required
            @input="handleInput"
          >
            <option
              v-for="(key, option) in relationshipOptions"
              :key="option"
              :value="option"
              v-text="key"
            ></option>
          </select>
        </div>

        <p v-if="relationshipErrors" class="label-input__errors">
          <span v-for="(error, index) in relationshipErrors" :key="index">
            {{ error }}
            <br v-show="index === relationshipErrors.length - 1" />
          </span>
        </p>
      </div>
    </div>

    <button class="birthdate-form__remove" type="button" @click="removeForm">
      {{ $t('BirthdateForm.remove') }}
    </button>
  </div>
</template>

<script>
export default {
  name: 'BirthdateForm',
  props: {
    form: {
      type: Object,
      required: true,
    },
    fieldName: {
      type: String,
      required: true,
    },
    genderOptions: {
      type: Object,
      default: () => {},
    },
    relationshipOptions: {
      type: Object,
      default: () => {},
    },
    serverErrors: {
      type: Object,
      default: () => {},
    },
  },
  computed: {
    value: {
      get() {
        return this.form;
      },
      set(value) {
        this.value = value;
      },
    },
    /**
     * @returns {Array|undefined}
     */
    firstNameErrors() {
      return this.getFieldErrors('first_name');
    },
    /**
     * @returns {Array|undefined}
     */
    birthdateErrors() {
      return this.getFieldErrors('birthdate');
    },
    /**
     * @returns {Array|undefined}
     */
    genderErrors() {
      return this.getFieldErrors('gender');
    },
    /**
     * @returns {Array|undefined}
     */
    relationshipErrors() {
      return this.getFieldErrors('relationship');
    },
  },
  methods: {
    removeForm() {
      this.$emit('removeForm');
    },
    handleInput() {
      this.$emit('input', this.value);
    },
    /**
     * @param {string} key
     * @returns {Array|undefined}
     */
    getFieldErrors(key) {
      return (this.serverErrors || {})[key];
    },
  },
};
</script>

<style lang="scss" scoped>
.birthdate-form {
  display: flex;
  flex-direction: column;
  gap: 1.25em;
  background-color: $color-gray-wild-sand;
  margin-top: 1em;
  padding: 1.875em;
  border-radius: 1.5625rem;

  &__row {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: flex-start;
    gap: 1.25em;

    @include breakpoint($breakpoint-m) {
      flex-direction: row;
      gap: 0.68em;
    }

    .label-input__input {
      background: $color-white;
    }
  }

  &__remove {
    border: none;
    background: none;
    color: $color-well-read;
    text-decoration: underline;
    margin-left: auto;
    font-size: 0.875em;
    padding: 0;
  }

  &__input {
    width: 100%;
    margin-top: 0;
  }
}
</style>
